import React, { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Autocomplete, {
  createFilterOptions,
} from "@mui/material/Autocomplete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import moment, { Moment } from "moment";
import toastr from "toastr";
import { makeGETRequestOnUserService } from "../../Api/api";
import Listbox from "../../components/Listbox";
import { IUser } from "../../components/YieldCalculator/Interfaces";
import {
  BOND_ORDER_HISTORY_ENDPOINTS,
  BondOrderHistoryResponse,
  dedupeBondOrders,
  getOrderInvestmentAmount,
  isExternalBondOrder,
  normalizeBondOrdersPayload,
} from "../../components/Users/Orders/helpers";
import {
  getOrderTimestamp,
  is_gsec_sdl_tBill,
  toINR,
} from "../../Helpers/functions";

type FinancialYearKey = "previous" | "current" | "next" | "all";

interface UserSelectionOption {
  uuid: string;
  fullName: string;
  mobileNumber: string;
  smestID: string;
  label: string;
  searchText: string;
}

interface FinancialYearOption {
  key: FinancialYearKey;
  title: string;
  subtitle: string;
  start: Moment;
  end: Moment;
}

interface GenieGroupedRow {
  isin: string;
  securityName: string;
  securityType: string;
  sources: string;
  orderCount: number;
  internalOrderCount: number;
  externalOrderCount: number;
  noOfBonds: number;
  investmentAmount: number;
  interestPreTds: number;
  interestPostTds: number;
  maturity: number;
  totalPreTds: number;
  totalPostTds: number;
}

interface SummaryCard {
  label: string;
  value: string;
}

const userFilter = createFilterOptions<UserSelectionOption>({
  ignoreCase: true,
  ignoreAccents: true,
  limit: 100,
  matchFrom: "any",
  stringify: (option) => option.searchText,
});

const getCurrentFinancialYearStartYear = () => {
  const today = moment();
  return today.month() >= 3 ? today.year() : today.year() - 1;
};

const buildFinancialYearOption = (
  key: Exclude<FinancialYearKey, "all">,
  startYear: number,
  titlePrefix: string
): FinancialYearOption => {
  const start = moment.utc(`${startYear}-04-01`, "YYYY-MM-DD").startOf("day");
  const end = moment
    .utc(`${startYear + 1}-03-31`, "YYYY-MM-DD")
    .endOf("day");
  return {
    key,
    title: `${titlePrefix} FY ${startYear}-${String(startYear + 1).slice(-2)}`,
    subtitle: `${start.format("DD MMM YYYY")} - ${end.format("DD MMM YYYY")}`,
    start,
    end,
  };
};

const getFinancialYearOptions = (): Record<FinancialYearKey, FinancialYearOption> => {
  const currentStartYear = getCurrentFinancialYearStartYear();
  const previous = buildFinancialYearOption(
    "previous",
    currentStartYear - 1,
    "Previous"
  );
  const current = buildFinancialYearOption(
    "current",
    currentStartYear,
    "Current"
  );
  const next = buildFinancialYearOption("next", currentStartYear + 1, "Next");

  return {
    previous,
    current,
    next,
    all: {
      key: "all",
      title: "All 3 FY",
      subtitle: `${previous.start.format("DD MMM YYYY")} - ${next.end.format(
        "DD MMM YYYY"
      )}`,
      start: previous.start.clone(),
      end: next.end.clone(),
    },
  };
};

const toNumber = (value?: number | string | null) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const isInternalBondOrder = (order: BondOrderHistoryResponse) =>
  !isExternalBondOrder(order);

const isInternalOrderDeleted = (order: BondOrderHistoryResponse) =>
  isInternalBondOrder(order) &&
  Boolean(order.is_delete) &&
  Boolean(order.order_deleted_date);

const getOrderDeletedMoment = (order: BondOrderHistoryResponse) => {
  if (!isInternalOrderDeleted(order)) return null;
  const deletedAt = moment.utc(order.order_deleted_date);
  return deletedAt.isValid() ? deletedAt : null;
};

const getRelevantCashflows = (order: BondOrderHistoryResponse) => {
  const cashflows = Array.isArray(order.cashflows) ? order.cashflows : [];
  if (!isInternalOrderDeleted(order)) {
    return cashflows.filter(
      (cashflow) =>
        cashflow.type === "Interest" || cashflow.type === "Maturity"
    );
  }

  const deletedAt = getOrderDeletedMoment(order);
  if (!deletedAt) {
    return cashflows.filter(
      (cashflow) =>
        cashflow.type === "Interest" || cashflow.type === "Maturity"
    );
  }

  return cashflows.filter((cashflow) => {
    if (!(cashflow.type === "Interest" || cashflow.type === "Maturity")) {
      return false;
    }
    const cashflowDate = moment.utc(cashflow.date);
    return cashflowDate.isValid() && cashflowDate.isSameOrBefore(deletedAt, "day");
  });
};

const shouldApplyTds = (order: BondOrderHistoryResponse) => {
  const taxCategory = (order.tax_category || "").trim().toLowerCase();
  return !is_gsec_sdl_tBill(order.security_type || "") && taxCategory !== "tax free";
};

const Users15Genie = () => {
  const [userOpen, setUserOpen] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [users, setUsers] = useState<UserSelectionOption[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserSelectionOption | null>(
    null
  );
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [orders, setOrders] = useState<BondOrderHistoryResponse[]>([]);
  const [financialYearKey, setFinancialYearKey] =
    useState<FinancialYearKey>("current");
  const [includeExternalBonds, setIncludeExternalBonds] = useState(true);

  const financialYearOptions = useMemo(() => getFinancialYearOptions(), []);
  const selectedFinancialYear = financialYearOptions[financialYearKey];

  const loadUsers = useCallback(async () => {
    if (usersLoading || usersLoaded) return;

    setUsersLoading(true);
    try {
      const response = await makeGETRequestOnUserService("/user/user_list/");
      if (response?.status === 200) {
        const options = (response.data as IUser[]).map((user) => {
          const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ");
          const smestId = user.smestID || "--";
          const mobileNumber = user.mobileNumber || "--";
          return {
            uuid: user.uuid,
            fullName,
            mobileNumber,
            smestID: smestId,
            label: `${fullName} - ${mobileNumber}`,
            searchText: `${fullName} ${mobileNumber} ${smestId}`.toLowerCase(),
          };
        });
        setUsers(options);
        setUsersLoaded(true);
      }
    } catch (error) {
      toastr.error("Failed to fetch users.");
    } finally {
      setUsersLoading(false);
    }
  }, [usersLoaded, usersLoading]);

  useEffect(() => {
    let mounted = true;

    const fetchOrders = async () => {
      if (!selectedUser?.uuid) {
        setOrders([]);
        return;
      }

      setOrdersLoading(true);
      try {
        const [externalHistory, ...internalHistories] = await Promise.allSettled([
          makeGETRequestOnUserService(
            `order_management_v2/external_orders/history/${selectedUser.uuid}/`
          ),
          ...BOND_ORDER_HISTORY_ENDPOINTS.map((buildUrl) =>
            makeGETRequestOnUserService(buildUrl(selectedUser.uuid))
          ),
        ]);

        const allFailed =
          externalHistory.status === "rejected" &&
          internalHistories.every((result) => result.status === "rejected");

        if (allFailed) {
          throw new Error("Unable to fetch any bond order history.");
        }

        const externalOrders =
          externalHistory.status === "fulfilled"
            ? normalizeBondOrdersPayload(externalHistory.value.data)
            : [];

        const internalOrders = internalHistories
          .filter(
            (
              result
            ): result is PromiseFulfilledResult<{
              data?: unknown;
            }> => result.status === "fulfilled"
          )
          .flatMap((result) => normalizeBondOrdersPayload(result.value.data));

        const combinedOrders = dedupeBondOrders([
          ...internalOrders,
          ...externalOrders,
        ]).sort((first, second) => getOrderTimestamp(second) - getOrderTimestamp(first));

        if (mounted) {
          setOrders(combinedOrders);
        }
      } catch (error) {
        if (mounted) {
          toastr.error("Failed to fetch user bond orders.");
          setOrders([]);
        }
      } finally {
        if (mounted) {
          setOrdersLoading(false);
        }
      }
    };

    fetchOrders();

    return () => {
      mounted = false;
    };
  }, [selectedUser]);

  const preparedOrders = useMemo(() => {
    return orders
      .filter(
        (order) => order.deal_status === "Settled" || isExternalBondOrder(order)
      )
      .filter((order) => !isInternalOrderDeleted(order))
      .filter((order) => includeExternalBonds || !isExternalBondOrder(order))
      .map((order) => {
        const relevantCashflows = getRelevantCashflows(order);
        if (!relevantCashflows.length) {
          return null;
        }

        return {
          ...order,
          cashflows: relevantCashflows,
        };
      })
      .filter(Boolean) as BondOrderHistoryResponse[];
  }, [includeExternalBonds, orders]);

  const groupedRows = useMemo(() => {
    const groups = new Map<string, GenieGroupedRow>();

    preparedOrders.forEach((order) => {
      const cashflowsInSelectedFinancialYear = (order.cashflows || []).filter(
        (cashflow) => {
          const cashflowDate = moment.utc(cashflow.date);
          return (
            cashflowDate.isValid() &&
            cashflowDate.isBetween(
              selectedFinancialYear.start,
              selectedFinancialYear.end,
              "day",
              "[]"
            )
          );
        }
      );

      if (!cashflowsInSelectedFinancialYear.length) {
        return;
      }

      const groupKey = order.isin || order.uuid;
      const existingGroup = groups.get(groupKey) || {
        isin: order.isin || "--",
        securityName: order.security_name || "--",
        securityType: order.security_type || "--",
        sources: "",
        orderCount: 0,
        internalOrderCount: 0,
        externalOrderCount: 0,
        noOfBonds: 0,
        investmentAmount: 0,
        interestPreTds: 0,
        interestPostTds: 0,
        maturity: 0,
        totalPreTds: 0,
        totalPostTds: 0,
      };

      existingGroup.securityName =
        existingGroup.securityName === "--" && order.security_name
          ? order.security_name
          : existingGroup.securityName;
      existingGroup.securityType =
        existingGroup.securityType === "--" && order.security_type
          ? order.security_type
          : existingGroup.securityType;
      existingGroup.orderCount += 1;
      existingGroup.noOfBonds += toNumber(order.no_of_bonds);
      existingGroup.investmentAmount += getOrderInvestmentAmount(order);

      if (isExternalBondOrder(order)) {
        existingGroup.externalOrderCount += 1;
      } else {
        existingGroup.internalOrderCount += 1;
      }

      cashflowsInSelectedFinancialYear.forEach((cashflow) => {
        const amount = toNumber(cashflow.amount);

        if (cashflow.type === "Interest") {
          existingGroup.interestPreTds += amount;
          existingGroup.interestPostTds += shouldApplyTds(order)
            ? amount * 0.9
            : amount;
        }

        if (cashflow.type === "Maturity") {
          existingGroup.maturity += amount;
        }
      });

      existingGroup.totalPreTds =
        existingGroup.interestPreTds + existingGroup.maturity;
      existingGroup.totalPostTds =
        existingGroup.interestPostTds + existingGroup.maturity;

      existingGroup.sources =
        existingGroup.internalOrderCount > 0 &&
        existingGroup.externalOrderCount > 0
          ? "Mixed"
          : existingGroup.externalOrderCount > 0
          ? "External"
          : "Internal";

      groups.set(groupKey, existingGroup);
    });

    return Array.from(groups.values()).sort(
      (first, second) => second.totalPostTds - first.totalPostTds
    );
  }, [preparedOrders, selectedFinancialYear]);

  const summaryCards = useMemo<SummaryCard[]>(() => {
    const summary = groupedRows.reduce(
      (accumulator, row) => {
        accumulator.interestPreTds += row.interestPreTds;
        accumulator.interestPostTds += row.interestPostTds;
        accumulator.maturity += row.maturity;
        accumulator.totalPostTds += row.totalPostTds;
        return accumulator;
      },
      {
        interestPreTds: 0,
        interestPostTds: 0,
        maturity: 0,
        totalPostTds: 0,
      }
    );

    return [
      {
        label: "ISIN Groups",
        value: String(groupedRows.length),
      },
      {
        label: "Interest Pre TDS",
        value: toINR(summary.interestPreTds, 2),
      },
      {
        label: "Interest Post TDS",
        value: toINR(summary.interestPostTds, 2),
      },
      {
        label: "Maturity",
        value: toINR(summary.maturity, 2),
      },
      {
        label: "Total Return Post TDS",
        value: toINR(summary.totalPostTds, 2),
      },
    ];
  }, [groupedRows]);

  return (
    <Box sx={{ maxWidth: "92%", mx: "auto", my: 4, zoom: 0.95 }}>
      <Paper sx={{ p: [2, 3], borderRadius: 4 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "flex-start",
            justifyContent: "space-between",
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
              Users 15 Genie
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Grouped FY-wise return view for bond holdings across internal and
              external orders.
            </Typography>
          </Box>
          <Box sx={{ width: ["100%", 520] }}>
            <Autocomplete
              id="users-15-genie-user-select"
              value={selectedUser}
              open={userOpen}
              options={users}
              loading={usersLoading}
              onOpen={() => {
                setUserOpen(true);
                loadUsers();
              }}
              onClose={() => setUserOpen(false)}
              onChange={(_, newValue) => setSelectedUser(newValue)}
              isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
              getOptionLabel={(option) => option.label}
              filterOptions={userFilter}
              ListboxComponent={Listbox}
              popupIcon={<ExpandMoreIcon />}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                      {option.fullName}
                    </Typography>
                    <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
                      {option.mobileNumber} | SMEST ID: {option.smestID}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select User by name or mobile number"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {usersLoading ? (
                          <CircularProgress color="inherit" size={18} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
        </Box>

        {selectedUser && (
          <>
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: 3,
                bgcolor: "rgba(118, 89, 238, 0.08)",
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700 }}>
                  {selectedUser.fullName}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {selectedUser.mobileNumber} | SMEST ID: {selectedUser.smestID}
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeExternalBonds}
                    onChange={(event) =>
                      setIncludeExternalBonds(event.target.checked)
                    }
                  />
                }
                label="Include External Bonds"
              />
            </Box>

            <Stack
              direction="row"
              spacing={1}
              sx={{
                flexWrap: "wrap",
                rowGap: 1,
                mb: 2,
              }}
            >
              {Object.values(financialYearOptions).map((option) => (
                <Chip
                  key={option.key}
                  label={option.title}
                  variant={
                    financialYearKey === option.key ? "filled" : "outlined"
                  }
                  color={financialYearKey === option.key ? "primary" : "default"}
                  onClick={() => setFinancialYearKey(option.key)}
                  sx={{ fontWeight: 700 }}
                />
              ))}
            </Stack>

            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mb: 3 }}
            >
              Viewing cashflows for {selectedFinancialYear.subtitle}. TDS is
              currently applied at 10% on interest cashflows for non-GSec /
              non-SDL / non-T-Bill holdings whose tax category is not Tax Free.
            </Typography>

            {ordersLoading ? (
              <Box
                sx={{
                  minHeight: 240,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {summaryCards.map((card) => (
                    <Grid item xs={12} sm={6} md={4} lg={2} key={card.label}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 3,
                          bgcolor: "#faf8ff",
                          border: "1px solid rgba(118, 89, 238, 0.12)",
                          height: "100%",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary", mb: 0.75 }}
                        >
                          {card.label}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                          {card.value}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ borderRadius: 3, overflowX: "auto" }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Security</TableCell>
                        <TableCell>Source</TableCell>
                        <TableCell align="right">Orders</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell align="right">Investment Amount</TableCell>
                        <TableCell align="right">Interest Pre TDS</TableCell>
                        <TableCell align="right">Interest Post TDS</TableCell>
                        <TableCell align="right">Maturity</TableCell>
                        <TableCell align="right">Total Pre TDS</TableCell>
                        <TableCell align="right">Total Post TDS</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groupedRows.length > 0 ? (
                        groupedRows.map((row) => (
                          <TableRow hover key={row.isin}>
                            <TableCell sx={{ minWidth: 260 }}>
                              <Typography
                                sx={{ fontWeight: 700, color: "info.main", mb: 0.25 }}
                              >
                                {row.securityName}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ color: "text.secondary" }}
                              >
                                {row.isin} | {row.securityType || "--"}
                              </Typography>
                            </TableCell>
                            <TableCell>{row.sources}</TableCell>
                            <TableCell align="right">{row.orderCount}</TableCell>
                            <TableCell align="right">{row.noOfBonds}</TableCell>
                            <TableCell align="right">
                              {toINR(row.investmentAmount, 2)}
                            </TableCell>
                            <TableCell align="right">
                              {toINR(row.interestPreTds, 2)}
                            </TableCell>
                            <TableCell align="right">
                              {toINR(row.interestPostTds, 2)}
                            </TableCell>
                            <TableCell align="right">
                              {toINR(row.maturity, 2)}
                            </TableCell>
                            <TableCell align="right">
                              {toINR(row.totalPreTds, 2)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>
                              {toINR(row.totalPostTds, 2)}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                              No bond cashflows found for the selected filters.
                            </Typography>
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                              Try another financial year or include external
                              bonds.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default Users15Genie;
